import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { pagedList } from 'v5/platform/lang/pagedList';

/** How long the rendered rows stay on the same pages before those pages are read. */
export const PAGES_SETTLE_MS = 120;

/**
 * The pages a virtualized list reads: the ones its rendered rows fall on. Each row reports its
 * place while it is rendered (`useRenderedRow`); the virtualizer renders what is on screen and a
 * little around, so nothing measures the scroll. A page is read once the rows have stayed on it
 * for `PAGES_SETTLE_MS`: a scroll passing over pages (a dragged scrollbar, a fast wheel) reads
 * only where it stops. Before any row has reported, the first page.
 */
export function useRenderedPages(pageSize: number) {
  const rendered = useRef(new Map<number, number>());
  const [pages, setPages] = useState<number[]>([0]);
  const wanted = useRef(pages);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const report = useCallback(
    (index: number) => {
      const follow = () => {
        const next = pagedList.pagesOf(rendered.current.keys(), pageSize);
        // nothing rendered (between two collections): keep reading what was read
        if (next.length === 0 || isEqual(next, wanted.current)) return;
        wanted.current = next;
        // a timer, not a frame: a list in a background tab still reads what it shows
        clearTimeout(timer.current);
        timer.current = setTimeout(
          () =>
            setPages(previous =>
              isEqual(previous, wanted.current) ? previous : wanted.current
            ),
          PAGES_SETTLE_MS
        );
      };
      rendered.current.set(index, (rendered.current.get(index) ?? 0) + 1);
      follow();
      return () => {
        const count = (rendered.current.get(index) ?? 1) - 1;
        if (count === 0) rendered.current.delete(index);
        else rendered.current.set(index, count);
        follow();
      };
    },
    [pageSize]
  );

  return { pages, report };
}

/** A row of a `useRenderedPages` list says where it is while it is rendered. */
export function useRenderedRow(
  index: number,
  report: (index: number) => () => void
) {
  useEffect(() => report(index), [index, report]);
}
